<template>
  <div class="operations-buttons">
    <div v-if="!isLoading && !response">
      <button
        id="operations-upload-button"
        :class="['button is-button-secondary is-rounded', isUploadActive ? 'is-primary' : '']"
        :disabled="!checkUserFeature('element.operations.button.cargar')"
        @click="isUploadActive = !isUploadActive">
        <span class="icon is-small">
          <i class="fas fa-upload" />
        </span>
        <span>{{ $t('operationsCharge') }}</span>
      </button>
    </div>

    <div v-if="isUploadActive && !isLoading" :class="[isUploadActive ? 'show' : 'hide']">
      <div
        v-if="!file && !response"
        :class="['dropZone card-item shadowless', dragging ? 'dropZone-over' : '']"
        @dragenter="dragging = true"
        @dragleave="dragging = false">
        <div class="dropZone-info" @drag="onChange">
          <span class="fa fa-cloud-upload dropZone-title" />
          <span class="dropZone-title">{{ $t('uploadFile') }}</span>
          <div class="dropZone-upload-limit-info">
            <span>{{ $t('fileShouldBe') }}</span>
            <span>{{ $t('fileShouldHaveSize') }}</span>
          </div>
        </div>
        <input id="operations-process-fileUpload" type="file" @change="onChange">
      </div>
      <div v-else-if="file && !response" class="dropZone-uploaded card-item shadowless">
        <div class="dropZone-uploaded-info">
          <div
            v-if="extension !== 'xlsx'"
            id="operations-process-error-format-title"
            class="dropZone-title">
            {{ $t('fileDoesNotHaveFormat') }}
            <div class="has-text-centered">
              <button
                id="operations-charge-again"
                type="button"
                class="button is-button-secondary is-rounded m-1"
                @click="removeFile">
                {{ $t('retry') }}
              </button>
            </div>
          </div>
          <div
            v-else-if="extension === 'xlsx' && !operations.length"
            id="operations-process-error-title"
            class="dropZone-title">
            {{ errors.fetch.message || $t('fileDoesNotHaveOperations') }}
            <div class="has-text-centered">
              <button
                id="operations-charge-xlsx-again"
                type="button"
                class="button is-button-secondary is-rounded m-1"
                @click="removeFile">
                {{ $t('retry') }}
              </button>
            </div>
          </div>
          <div
            v-else
            id="operations-process-success"
            class="dropZone-title">
            {{ $t('fileUploadedSuccessfully') }}
          </div>
          <div class="uploadedFile-info">
            <div>{{ $t('fileName') }} {{ file.name }}</div>
            <div>{{ $t('fileSize') }} {{ file.size/1000000 }} MB</div>
            <div>{{ $t('fileExtension') }} .{{ extension }}</div>
          </div>

          <div v-if="operations.length" class="process-operations">
            <div class="has-text-centered">
              <p>
                {{ $t('willBeProccessed') }} <strong> {{ operations.length }} {{ $t('operations') }}</strong>. <br>
              </p>
              <p>
                {{ $t('areYouSure') }}
              </p>
            </div>
            <div>
              <button
                type="button"
                class="button is-button-secondary is-rounded m-1"
                @click="removeFile">
                {{ $t('cancel') }}
              </button>
              <button
                id="operation-proccess-button"
                type="button"
                class="button is-button-primary is-rounded m-1"
                @click="process">
                {{ $t('process') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="response" class="dropZone-uploaded card-item shadowless process-operations">
        <div>
          {{ $t('wereCreated') }} <strong>{{ response.successes }}</strong> {{ $t('successOperations') }},
          {{ $t('failedOperations') }} <strong> {{ response.failures }} </strong> {{ $t('operations') }}.
        </div>
        <b-button rounded type="is-primary is-light" class="show-response-details-button" @click="showResponseDetails()">
          <span class="content-title">{{ $t('seeDetail') }}</span> <b-icon
            size="is-small"
            :icon="responseDetails ? 'angle-up' : 'angle-down'" />
        </b-button>
        <div v-if="responseDetails" id="response-details" class="response-details">
          <li v-for="operation in response.responses.filter(op => !!op.success)" :key="operation.companyOperationId">
            {{ `${$t('operationWasCreated') + ' ' + operation.companyOperationId}` }}
          </li>
          <li v-for="message in response.errors" :key="message" class="response-error">
            {{ message }}
          </li>
        </div>
        <button
          id="operation-finish-button"
          type="button"
          class="button is-button-primary is-rounded"
          @click="response = undefined, isUploadActive = false, close()">
          {{ $t('close') }}
        </button>
      </div>
    </div>
    <div v-if="!isLoading && !hide" class="columns mt-1">
      <FindEntitiesInput
        id="operations-search-text"
        :placeholder="$t('searchOperations')"
        :expanded="true"
        :finder="finder"
        @select="$emit('clicked-on-filtered-operation', $event.companyOperationId)" />
      <button
        id="operations-refresh-button"
        :class="['button is-button-secondary is-rounded ml-1', isUploadActive ? 'is-primary' : '']"
        :disabled="!checkUserFeature('element.operations.button.refresh')"
        @click="$emit('clicked-on-refresh')">
        <span class="icon is-small">
          <i class="fa-solid fa-arrows-rotate" />
        </span>
      </button>
      <b-dropdown v-model="selectedOptions" aria-role="list" class="ml-2">
        <button id="filter-button-dropdown" slot="trigger" slot-scope="{ active }" class="button is-primary">
          <span>{{ selectedOptions }}</span>
          <b-icon :icon="active ? 'angle-up' : 'angle-down'" />
        </button>
        <b-dropdown-item id="filter-button-all" :value="$t('allFilter')" aria-role="listitem" @click="$emit('all-filtered')">
          {{ $t('allFilter') }}
        </b-dropdown-item>
        <b-dropdown-item id="filter-button-mortgage" :value="$t('mortgageFilter')" aria-role="listitem" @click="$emit('mortgage-filtered')">
          {{ $t('mortgageFilter') }}
        </b-dropdown-item>
        <b-dropdown-item id="filter-button-sme" :value="$t('smeFilter')" aria-role="listitem" @click="$emit('sme-filtered')">
          {{ $t('smeFilter') }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="$i18n.locale === 'br'"
          id="filter-button-home-equity"
          :value="$t('homeEquityFilter')"
          aria-role="listitem"
          @click="$emit('home-equity-filtered')">
          {{ $t('homeEquityFilter') }}
        </b-dropdown-item>
        <b-dropdown-item
          id="filter-button-complementary"
          :value="$t('complementaryFilter')"
          aria-role="listitem"
          @click="$emit('complementary-filtered')">
          {{ $t('complementaryFilter') }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="$i18n.locale === 'cl'"
          id="filter-button-parking"
          :value="$t('parkingFilter')"
          aria-role="listitem"
          @click="$emit('parking-filtered')">
          {{ $t('parkingFilter') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <Spinner v-if="isLoading" />
    <div v-if="isLoading"> {{ `${$t('processing') + ' ' + currentOperation.companyOperationId}` }}</div>
  </div>
</template>

<script>
/* eslint-disable no-console */

import { IdNumberValidatorCL } from 'creditu-types/validators/id-number';
import { formattedToValue, valueToFormatted } from 'creditu-types/format';
import FindEntitiesInput from '@/ui/components/internal/FindEntitiesInput.vue';
import notification from '@/ui/mixins/notification';
import { getOperationsFromXLSFile, findOperationsBySearchString, createBulkOperationsLimited } from '@/application/services/api-clients/operations';
import userFeatures from '@/ui/mixins/user-features';
import { getInvestors } from '@/application/services/api-clients/investors';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';
import Spinner from '@/ui/components/Spinner.vue';
import { validatePhoneNumber } from '../../../../infrastructure/format-phone-number';

export default {
  name: 'OperationsButtons',
  components: { Spinner, FindEntitiesInput },
  mixins: [notification, userFeatures],
  props: {
    operationType: { type: String },
    hide: { type: Boolean }
  },
  data() {
    return {
      isLoading: false,
      isUploadActive: false,
      finder: (val, cancel) => findOperationsBySearchString(val, cancel, undefined, undefined, undefined, undefined, this.operationType),
      dragging: false,
      file: '',
      isComponentModalActive: false,
      operations: [],
      response: undefined,
      responseDetails: false,
      errors: {
        fetch: { isError: false, message: '' },
        process: { isError: false, message: '' }
      },
      currentOperation: { companyOperationId: 'nnn' },
      investors: [],
      operationsToCreateIds: [],
      validatedPhoneNumbers: [],
      typeFilter: 'ALL',
      selectedOptions: 'Filtrar'
    };
  },
  computed: {
    extension() {
      return (this.file) ? this.file.name.split('.').pop() : '';
    },
    isI18n() { return ['br', 'cl', 'pe'].includes(this.$i18n.locale); }
  },
  methods: {
    onChange(event) {
      const files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        this.dragging = false;
        return;
      }
      const file = files[0];
      if (file.size > 5000000) {
        this.alert({ message: 'Revisa que el archivo no supere los 10 MB de tamaño' });
        this.dragging = false;
        return;
      }
      this.file = file;
      this.dragging = false;
      this.getOperationsFile();
    },
    async setInvestorsIds(operations) {
      this.investors = await getInvestors();
      this.investors.forEach(investor => {
        investor.tags.push(investor.name);
      });
      // eslint-disable-next-line no-param-reassign
      operations.forEach(op => {
        // eslint-disable-next-line no-param-reassign
        const investor = this.investors.find(invest => invest.tags.includes(op.investorName || op.investorId));
        if (!investor || !investor._id) {
          this.errorMessage = 'nombre de inversionista desconocido'; // Esto se podría traducir
          throw new Error(this.errorMessage);
        }
        // eslint-disable-next-line no-param-reassign
        op.investorId = (investor || {})._id;
      });
      return operations;
    },
    async setFirstDueDateMonthsToAdd(operations) {
      const firstDueDateMonthsToAddToggle = await getFeatureToggleByName('first-duedate-months-to-add') || { options: [{}] };
      const { isActive } = firstDueDateMonthsToAddToggle;
      const { value } = firstDueDateMonthsToAddToggle.options[0];
      let operationsOutput = operations;
      if (isActive && value) {
        operationsOutput = operations.map(operation => {
          // eslint-disable-next-line no-param-reassign
          operation.firstDueDateMonthsToAdd = value;
          return operation;
        });
      }
      return operationsOutput;
    },
    validateIdNumber(rut) {
      if (!rut) return { validIdNumber: true, formattedIdNumber: null };
      const idNumberValidatorCL = new IdNumberValidatorCL();
      const unformattedIdNumber = formattedToValue('idNumber', this.$i18n.locale, rut);
      const validIdNumber = idNumberValidatorCL.validate(unformattedIdNumber);
      const formattedIdNumber = valueToFormatted('idNumber', this.$i18n.locale, unformattedIdNumber);
      return { validIdNumber, formattedIdNumber };
    },
    validateIdNumbers(operations) {
      if (this.$i18n.locale === 'cl') {
        operations.forEach(operation => {
          const { validIdNumber: validDebtorIdNumber, formattedIdNumber: formattedDebtorIdNumber } = this.validateIdNumber(operation.debtorIdNumber);
          const { validIdNumber: validCodebtorIdNumber, formattedIdNumber: formattedCodebtorIdNumber } = this.validateIdNumber(operation.codebtorIdNumber);
          if (!validDebtorIdNumber || !validCodebtorIdNumber) {
            this.errorMessage = `rut inválidos: ${!validDebtorIdNumber ? operation.debtorIdNumber : ''}${!validCodebtorIdNumber ? ` ${operation.codebtorIdNumber}` : ''}`;
            throw new Error(this.errorMessage);
          }
          // eslint-disable-next-line no-param-reassign
          operation.debtorIdNumber = formattedDebtorIdNumber;
          // eslint-disable-next-line no-param-reassign
          operation.codebtorIdNumber = formattedCodebtorIdNumber || undefined; // || operation.debtorIdNumber;
        });
      }
      return operations;
    },
    validatePhoneNumberProperties(country, operations) {
      const results = [];
      operations.forEach(operation => {
        if (operation.phoneNumber === undefined) {
          // eslint-disable-next-line no-param-reassign
          operation.phoneNumber = '';
        }
        if (validatePhoneNumber[country](operation.phoneNumber) !== true) {
          results.push({
            companyOperationId: operation.companyOperationId,
            phoneNumber: operation.phoneNumber,
            property: operation.property
          });
        }
      });
      return results;
    },
    validatePhoneNumbers(operations) {
      const country = this.$i18n.locale;
      const processedOperations = [];
      if (country === 'cl') {
        operations.forEach(operation => {
          if (operation.type === 'MORTGAGE') {
            processedOperations.push([
              { companyOperationId: operation.companyOperationId,
                phoneNumber: operation.debtorTelephone,
                property: 'debtorTelephone' },
              { companyOperationId: operation.companyOperationId,
                phoneNumber: operation.codebtorAddressTelephone,
                property: 'codebtorAddressTelephone' }
            ]);
          }
          if (operation.type === 'SME') {
            if (operation.fieldsFromRegisterProcess) {
              processedOperations.push([
                { companyOperationId: operation.companyOperationId,
                  phoneNumber: operation.fieldsFromRegisterProcess.debtor.particularPhoneNumber,
                  property: 'fieldsFromRegisterProcess.debtor.particularPhoneNumber' },
                { companyOperationId: operation.companyOperationId,
                  phoneNumber: operation.fieldsFromRegisterProcess.debtor.commercialPhoneNumber,
                  property: 'fieldsFromRegisterProcess.debtor.commercialPhoneNumber' },
                { companyOperationId: operation.companyOperationId,
                  phoneNumber: operation.fieldsFromRegisterProcess.debtor.cellphoneNumber,
                  property: 'fieldsFromRegisterProcess.operation.debtor.cellphoneNumber' },
                { companyOperationId: operation.companyOperationId,
                  phoneNumber: operation.fieldsFromRegisterProcess.debtor.employerPhoneNumber,
                  property: 'fieldsFromRegisterProcess.debtor.employerPhoneNumber' }
              ]);
            } else {
              processedOperations.push([
                { companyOperationId: operation.companyOperationId,
                  phoneNumber: operation.debtorPhone,
                  property: 'debtorPhone' }
              ]);
            }
          }
        });
      }
      if (country === 'br') {
        operations.forEach(operation => {
          processedOperations.push([
            { companyOperationId: operation.companyOperationId,
              phoneNumber: operation.phone,
              property: 'phone' }
          ]);
        });
      }
      return this.validatePhoneNumberProperties(country, processedOperations.flat());
    },
    getPhoneNumberValidationMessage(validatedPhoneNumbers) {
      const country = this.$i18n.locale;
      // eslint-disable-next-line no-nested-ternary
      const example = country === 'cl' ? '56912345678' : country === 'br' ? '5512345678901' : '51912345678';
      if (validatedPhoneNumbers.length > 0) {
        return [`${this.$t('detailMessage1')} (${example}). ${this.$t('detailMessage2')}`].join('\r\n');
      }
      return undefined;
    },
    getValidatedPhoneNumbersModal(validatedPhoneNumbers) {
      if (validatedPhoneNumbers.length > 0) {
        this.$buefy.dialog.confirm({
          title: this.$t('important'),
          message: this.getPhoneNumberValidationMessage(validatedPhoneNumbers),
          type: 'is-danger',
          hasIcon: true,
          cancelText: this.$t('detail'),
          confirmText: this.$t('continue'),
          onCancel: () => this.getDetails(validatedPhoneNumbers)
        });
      }
    },
    async getOperationsFile() {
      this.isLoading = true;
      try {
        this.errors.fetch.isError = false;
        this.errorMessage = undefined;
        this.operations = await getOperationsFromXLSFile(this.file, this.$i18n.locale);
        this.operations = await this.setInvestorsIds(this.operations);
        this.operations = await this.setFirstDueDateMonthsToAdd(this.operations);
        this.operations = this.validateIdNumbers(this.operations);
        this.operationsToCreateIds = this.operations.map(op => { return op.companyOperationId; });
        this.validatedPhoneNumbers = this.validatePhoneNumbers(this.operations);
        this.getValidatedPhoneNumbersModal(this.validatedPhoneNumbers);
        this.isLoading = false;
      } catch (e) {
        this.operations = [];
        this.errors.fetch.isError = true;
        this.errors.fetch.message = e.message.includes('invalid') ? `Revisa el nombre de las columnas del archivo (${e.message}).` : e.message; // this.errorMessage;
        this.isLoading = false;
      }
    },
    getDetails(validatedPhoneNumbers) {
      if (validatedPhoneNumbers.length > 0) {
        const lines = [Object.keys(validatedPhoneNumbers[0]).join('\t')];
        validatedPhoneNumbers.forEach(phoneNumber => {
          const line = Object.values(phoneNumber).join('\t');
          lines.push(line);
        });
        const textToCopy = lines.join('\n');
        this.$copyText(textToCopy).then(result => {
          this.$emit('copySuccess', result);
        }).catch(error => {
          this.$emit('copyError', error);
        });
      }
    },
    removeFile() {
      this.file = '';
      this.operations = [];
    },
    async process() {
      this.isLoading = true;
      try {
        this.response = await createBulkOperationsLimited(this.operations, this.currentOperation);
        const createdOperationsIds = this.response.responses.map(createdOperation => createdOperation.companyOperationId);
        this.$emit('get-operations', createdOperationsIds);
        this.operations = [];
        this.file = '';
        this.isLoading = false;
      } catch (e) {
        this.errors.process.isError = true;
        this.errors.process.message = e.message;
        this.isLoading = false;
      }
    },
    showResponseDetails() {
      this.responseDetails = !this.responseDetails;
    },
    close() {
      this.$emit('refresh');
    }
  }
};
</script>
<style scoped>
.operations-buttons {
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
  align-content: space-between;
  gap: 1rem;
}
.hide {
  max-height: 0;
  overflow: hidden;
  padding: 0;
  opacity: 0;
}
.show {
  max-height: 100rem;
  overflow: hidden;
}
.response-error {
  color:var(--secondary);
}
.process-operations,
.dropZone-info {
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}
.dropZone {
  border: 2px dashed #eee;
}

.dropZone:hover {
  border: 2px solid var(--primary);
}

.dropZone:hover .dropZone-title {
  color: var(--primary);
}

.dropZone-info {
  color: #A8A8A8;
  text-align: center;
}

.dropZone-title {
  color: #787878;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.dropZone-upload-limit-info {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
}

.dropZone-over {
  background: #5C5C5C;
  opacity: 0.8;
}

.dropZone-uploaded {
  width: 100%;
  position: relative;
  border: 2px dashed #eee;
}

.dropZone-uploaded-info {
  display: flex;
  flex-flow: column wrap;
  align-items: center;
  justify-content: center;
  padding: 5%;
  gap: 1rem;
  color: #A8A8A8;
}

</style>
<i18n>
{
  "cl": {
    "operationsCharge": "Cargar Operaciones",
    "uploadFile": "Arrastra un archivo o clickea aquí",
    "fileShouldBe": "Debe ser un archivo .xlxs",
    "fileShouldHaveSize": "Tamaño máximo de 10 MB",
    "fileDoesNotHaveFormat": "El archivo no tiene el formato esperado.",
    "retry": "Reintentar",
    "fileDoesNotHaveOperations": "El archivo no tiene operaciones para cargar.",
    "fileUploadedSuccessfully": "Archivo cargado exitosamente.",
    "fileName": "Nombre:",
    "fileSize": "Tamaño:",
    "fileExtension": "Extensión:",
    "willBeProccessed": "Se procesará(n)",
    "operations": "operaciones",
    "areYouSure": "¿Estás seguro que quieres continuar?",
    "wereCreated": "Se crearon",
    "successOperations": "operaciones con éxito",
    "failedOperations": "fallaron",
    "seeDetail": "Ver Detalle",
    "operationWasCreated": "Se creó la operación",
    "close": "Finalizar",
    "cancel": "Cancelar",
    "process": "Procesar",
    "searchOperations": "Buscar operación",
    "processing": "Procesando operación ",
    "important": "Importante",
    "detail": "Copiar información al portapapeles",
    "continue": "Continuar",
    "detailMessage1": "Considera que este archivo tiene números telefónicos con formato diferente al esperado",
    "detailMessage2": "Si deseas continuar de todas formas, presiona Continuar y luego haz click en Procesar.",
    "allFilter": "Todas",
    "mortgageFilter": "Hipotecario",
    "smeFilter": "SME",
    "homeEquityFilter": "Home equity",
    "complementaryFilter": "Complementario",
    "parkingFilter": "Estacionamiento"
  },
  "pe": {
    "operationsCharge": "Cargar Operaciones",
    "uploadFile": "Arrastra un archivo o clickea aquí",
    "fileShouldBe": "Debe ser un archivo .xlxs",
    "fileShouldHaveSize": "Tamaño máximo de 10 MB",
    "fileDoesNotHaveFormat": "El archivo no tiene el formato esperado.",
    "retry": "Reintentar",
    "fileDoesNotHaveOperations": "El archivo no tiene operaciones para cargar.",
    "fileUploadedSuccessfully": "Archivo cargado exitosamente.",
    "fileName": "Nombre:",
    "fileSize": "Tamaño:",
    "fileExtension": "Extensión:",
    "willBeProccessed": "Se procesará(n)",
    "operations": "Operaciones",
    "areYouSure": "¿Estás seguro que quieres continuar?",
    "wereCreated": "Se crearon",
    "successOperations": "operaciones con éxito",
    "failedOperations": "fallaron",
    "seeDetail": "Ver Detalle",
    "operationWasCreated": "Se creó la operación",
    "close": "Finalizar",
    "cancel": "Cancelar",
    "process": "Procesar",
    "searchOperations": "Buscar operación",
    "processing": "Procesando operación ",
    "important": "Importante",
    "detail": "Copiar información al portapapeles",
    "continue": "Continuar",
    "detailMessage1": "Considera que este archivo tiene números telefónicos con formato diferente al esperado",
    "detailMessage2": "Si deseas continuar de todas formas, presiona Continuar y luego haz click en Procesar.",
    "allFilter": "Todas",
    "mortgageFilter": "Hipotecario",
    "smeFilter": "SME",
    "homeEquityFilter": "Home equity",
    "complementaryFilter": "Complementario",
    "parkingFilter": "Estacionamiento"
  },
  "br": {
    "operationsCharge": "Carregar Operações",
    "uploadFile": "Arraste um arquivo ou clique aqui",
    "fileShouldBe": "Deve ser um arquivo .xlxs",
    "fileShouldHaveSize": "Tamanho máximo de 10 MB",
    "fileDoesNotHaveFormat": "O arquivo não está no formato esperado..",
    "retry": "Tente novamente",
    "fileDoesNotHaveOperations": "O arquivo não tem operações para carregar.",
    "fileUploadedSuccessfully": "Arquivo carregado com sucesso",
    "fileName": "Nome:",
    "fileSize": "Tamanho:",
    "fileExtension": "Extensão:",
    "willBeProccessed": "Será processado",
    "operations": "Operações",
    "areYouSure": "¿Você tem certeza que quer continuar?",
    "wereCreated": "Foram criados",
    "successOperations": "operações con éxito",
    "failedOperations": "falharam",
    "seeDetail": "Ver Detalle",
    "operationWasCreated": "A operação foi criada",
    "close": "Finalizar",
    "cancel": "Cancelar",
    "process": "Processar",
    "searchOperations": "Busca operações",
    "processing": "operação de processamento ",
    "important": "Importante",
    "detail": "Copiar informações para Clipboard",
    "continue": "Continuar",
    "detailMessage1": "Considere que este arquivo tem vários telefones com formato diferente do esperado",
    "detailMessage2": "Se você deseja continuar de todas as formas, pressione Continuar e depois clique em Processar.",
    "allFilter": "Todas",
    "mortgageFilter": "Hipoteca",
    "smeFilter": "SME",
    "homeEquityFilter": "Home equity",
    "complementaryFilter": "Complementar",
    "parkingFilter": "Estacionamiento"
  }
}
</i18n>
